import React from 'react';
import PropTypes from "prop-types";
export var DataGridColumn = function DataGridColumn() {
  return React.createElement("div", null);
};
DataGridColumn.propTypes = {
  /**
     * The unique identifier for this column
     */
  id: PropTypes.string.isRequired,

  /**
     * A `ReactNode` used when rendering the column header. When providing complicated content, please make sure to utilize CSS to respect truncation as space allows. Check the docs example.
     */
  display: PropTypes.node,

  /**
     * A Schema to use for the column. Built-in values are ['boolean', 'currency', 'datetime', 'numeric', 'json'] but can be expanded by defining your own #EuiDataGrid `schemaDetectors` (for in-memory detection). In general, it is advised to pass in a value here when you are sure of the schema ahead of time, so that you don't need to rely on the automatic detection.
     */
  schema: PropTypes.string,

  /**
     * Defauls to true. Defines shether or not the column's cells can be expanded with a popup onClick / keydown.
     */
  isExpandable: PropTypes.bool,

  /**
     * Whether this column's width can be changed by the user, defaults to true
     */
  isResizable: PropTypes.bool,

  /**
     * Initial width (in pixels) of the column
     */
  initialWidth: PropTypes.number,

  /**
     * Whether this column is sortable
     */
  isSortable: PropTypes.bool,

  /**
     * Default sort direction of the column
     */
  defaultSortDirection: PropTypes.oneOf(["asc", "desc"])
};
export var DataGridPagination = function DataGridPagination() {
  return React.createElement("div", null);
};
DataGridPagination.propTypes = {
  /**
     * The index of the current page, starts at 0 for the first page
     */
  pageIndex: PropTypes.number.isRequired,

  /**
     * How many rows should initially be shown per page
     */
  pageSize: PropTypes.number.isRequired,

  /**
     * An array of page sizes the user can select from
     */
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,

  /**
     * A callback for when the user changes the page size selection
     */
  onChangeItemsPerPage: PropTypes.func.isRequired,

  /**
     * A callback for when the current page index changes
     */
  onChangePage: PropTypes.func.isRequired
};
export var DataGridSorting = function DataGridSorting() {
  return React.createElement("div", null);
};
DataGridSorting.propTypes = {
  /**
     * A function that receives updated column sort details in response to user interactions in the toolbar controls
     */
  onSort: PropTypes.func.isRequired,

  /**
     * An array of the column ids currently being sorted and their sort direction. The array order determines the sort order. `{ id: 'A'; direction: 'asc' }`
     */
  columns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(["asc", "desc"]).isRequired
  }).isRequired).isRequired
};
export var DataGridInMemory = function DataGridInMemory() {
  return React.createElement("div", null);
};
DataGridInMemory.propTypes = {
  /**
      Given the data flow Sorting->Pagination:
      Each step can be performed by service calls or in-memory by the grid.
      However, we cannot allow any service calls after an in-memory operation.
      E.g. if Pagination requires a service call the grid cannot perform
      in-memory Sorting. This means a single value representing the
      service / in-memory boundary can be used. Thus there are four states for in-memory's level:
      * "enhancements" - no in-memory operations, but use the available data to enhance the grid
      * "pagination" - only pagination is performed in-memory
      * "sorting" - sorting & pagination is performed in-memory
   */
  level: PropTypes.oneOf(["enhancements", "pagination", "sorting"]).isRequired,

  /**
     * An array of column ids for the in-memory processing to skip
     */
  skipColumns: PropTypes.arrayOf(PropTypes.string.isRequired)
};
export var DataGridStyle = function DataGridStyle() {
  return React.createElement("div", null);
};
DataGridStyle.propTypes = {
  /**
     * Size of fonts used within the row and column cells
     */
  fontSize: PropTypes.oneOf(["s", "m", "l"]),

  /**
     * Border uses for the row and column cells
     */
  border: PropTypes.oneOf(["all", "horizontal", "none"]),

  /**
     * If set to true, rows will alternate zebra striping for clarity
     */
  stripes: PropTypes.bool,

  /**
     * Visual style for the column headers. Recommendation is to use the `underline` style in times when #EuiDataGrid `toolbarVisibility` is set to `false`.
     */
  header: PropTypes.oneOf(["shade", "underline"]),

  /**
     * Will define what visual style to show on row hover
     */
  rowHover: PropTypes.oneOf(["highlight", "none"]),

  /**
     * Defines the padding with the row and column cells
     */
  cellPadding: PropTypes.oneOf(["s", "m", "l"])
};
export var DataGridToolbarVisibilityOptions = function DataGridToolbarVisibilityOptions() {
  return React.createElement("div", null);
};
DataGridToolbarVisibilityOptions.propTypes = {
  /**
     * Allows the ability for the user to hide fields and sort columns, boolean or a #EuiDataGridToolBarVisibilityColumnSelectorOptions
     */
  showColumnSelector: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.shape({
    /**
       * When `false`, removes the ability to show & hide columns through the UI
       */
    allowHide: PropTypes.bool,

    /**
       * When `false`, removes the ability to re-order columns through the UI
       */
    allowReorder: PropTypes.bool
  }).isRequired]),

  /**
     * Allows the ability for the user to set the grid density. If on, this merges against what is provided in #EuiDataGridStyle
     */
  showStyleSelector: PropTypes.bool,

  /**
     * Allows the ability for the user to sort rows based upon column values
     */
  showSortSelector: PropTypes.bool,

  /**
     * Allows user to be able to full screen the data grid. If set to `false` make sure your grid fits within a large enough panel to still show the other controls.
     */
  showFullScreenSelector: PropTypes.bool,

  /**
     * Will place any passed node into the toolbar in front of the fullscreen button. Recommend using EuiButtonEmpty with the props shown in the examples.
     */
  additionalControls: PropTypes.node
};
export var DataGridCellValueElement = function DataGridCellValueElement() {
  return React.createElement("div", null);
};
DataGridCellValueElement.propTypes = {
  /**
     * index of the row being rendered, 0 represents the first row. This index always includes
     * pagination offset, meaning the first rowIndex in a grid is `pagination.pageIndex * pagination.pageSize`
     * so take care if you need to adjust the rowIndex to fit your data
     */
  rowIndex: PropTypes.number.isRequired,

  /**
     * id of the column being rendered, the value comes from the #EuiDataGridColumn `id`
     */
  columnId: PropTypes.string.isRequired,

  /**
     * callback function to set custom props & attributes on the cell's wrapping `div` element;
     * it's best to wrap calls to `setCellProps` in a `useEffect` hook
     */
  setCellProps: PropTypes.func.isRequired,

  /**
     * whether or not the cell is expandable, comes from the #EuiDataGridColumn `isExpandable` which defaults to `true`
     */
  isExpandable: PropTypes.bool.isRequired,

  /**
     * whether or not the cell is expanded
     */
  isExpanded: PropTypes.bool.isRequired,

  /**
     * when rendering the cell, `isDetails` is false; when the cell is expanded, `renderCellValue` is called again to render into the details popover and `isDetails` is true
     */
  isDetails: PropTypes.bool.isRequired
};
export var DataGridSchemaDetector = function DataGridSchemaDetector() {
  return React.createElement("div", null);
};
DataGridSchemaDetector.propTypes = {
  /**
     * The name of this data type, matches #EuiDataGridColumn / schema `schema`
     */
  type: PropTypes.string.isRequired,

  /**
     * The function given the text value of a cell and returns a score of [0...1] of how well the value matches this data type
     */
  detector: PropTypes.func.isRequired,

  /**
     * A custom comparator function when performing in-memory sorting on this data type, takes `(a: string, b: string, direction: 'asc' | 'desc) => -1 | 0 | 1`
     */
  comparator: PropTypes.func,

  /**
     * The icon used to visually represent this data type. Accepts any `EuiIcon IconType`.
     */
  icon: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "aggregate", "alert", "annotation", "apmApp", "apmTrace", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "asterisk", "auditbeatApp", "beaker", "bell", "bellSlash", "bolt", "boxesHorizontal", "boxesVertical", "branch", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "codeApp", "check", "checkInCircleFilled", "cheer", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "compute", "console", "consoleApp", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "crossInACircleFilled", "currency", "cut", "dashboardApp", "database", "dataVisualizer", "devToolsApp", "discoverApp", "document", "documentEdit", "documents", "dot", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignLeft", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "flag", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "fullScreen", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexSettings", "inputOutput", "inspect", "invert", "ip", "keyboardShortcut", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "lensApp", "link", "list", "listAdd", "lock", "lockOpen", "logsApp", "logoAerospike", "logoApache", "logoAPM", "logoAppSearch", "logoAWS", "logoAWSMono", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticsearch", "logoElasticStack", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuLeft", "menuRight", "merge", "metricbeatApp", "metricsApp", "minimize", "minusInCircle", "minusInCircleFilled", "monitoringApp", "moon", "nested", "node", "notebookApp", "number", "offline", "online", "package", "packetbeatApp", "pageSelect", "pagesSelect", "partial", "paperClip", "pause", "pencil", "pin", "pinFilled", "pipelineApp", "play", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "reporter", "reportingApp", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "shard", "share", "snowflake", "sortable", "sortDown", "sortLeft", "sortRight", "sortUp", "spacesApp", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "swatchInput", "symlink", "tableOfContents", "tableDensityExpanded", "tableDensityCompact", "tableDensityNormal", "tag", "tear", "temperature", "timeline", "timelionApp", "training", "trash", "upgradeAssistantApp", "uptimeApp", "user", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visualizeApp", "visVega", "visVisualBuilder", "watchesApp", "wrench", "tokenClass", "tokenProperty", "tokenEnum", "tokenVariable", "tokenMethod", "tokenAnnotation", "tokenException", "tokenInterface", "tokenParameter", "tokenField", "tokenElement", "tokenFunction", "tokenBoolean", "tokenString", "tokenArray", "tokenNumber", "tokenConstant", "tokenObject", "tokenEvent", "tokenKey", "tokenNull", "tokenStruct", "tokenPackage", "tokenOperator", "tokenEnumMember", "tokenRepo", "tokenSymbol", "tokenFile", "tokenModule", "tokenNamespace", "tokenDate", "tokenIP", "tokenNested", "tokenAlias", "tokenShape", "tokenGeo", "tokenRange", "tokenBinary", "tokenJoin", "tokenPercolator", "tokenFlattened", "tokenRankFeature", "tokenRankFeatures", "tokenKeyword", "tokenCompletionSuggester", "tokenDenseVector", "tokenText", "tokenTokenCount", "tokenSearchType", "tokenHistogram"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]).isRequired,

  /**
     * The color associated with this data type; it's used to color the icon token
     */
  color: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.oneOf(["euiColorVis0", "euiColorVis1", "euiColorVis2", "euiColorVis3", "euiColorVis4", "euiColorVis5", "euiColorVis6", "euiColorVis7", "euiColorVis8", "euiColorVis9", "gray"]).isRequired, PropTypes.string.isRequired]).isRequired, PropTypes.string.isRequired]),

  /**
     * Text for how to represent an ascending sort of this data type, e.g. 'A -> Z'
     */
  sortTextAsc: PropTypes.node.isRequired,

  /**
     * Text for how to represent a descending sort of this data type, e.g. 'Z -> A'
     */
  sortTextDesc: PropTypes.node.isRequired,

  /**
     * Whether this column is sortable (defaults to true)
     */
  isSortable: PropTypes.bool,

  /**
     * Default sort direction of the column
     */
  defaultSortDirection: PropTypes.oneOf(["asc", "desc"])
};
export var DataGridColumnVisibility = function DataGridColumnVisibility() {
  return React.createElement("div", null);
};
DataGridColumnVisibility.propTypes = {
  /**
     * An array of #EuiDataGridColumn `id`s dictating the order and visibility of columns.
     */
  visibleColumns: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,

  /**
     * A callback for when a column's visibility or order is modified by the user.
     */
  setVisibleColumns: PropTypes.func.isRequired
};
export var DataGridPopoverContent = function DataGridPopoverContent() {
  return React.createElement("div", null);
};
DataGridPopoverContent.propTypes = {
  /**
     * your `cellValueRenderer` as a ReactElement; allows wrapping the rendered content: `({children}) => <div>{children}</div>`
     */
  children: PropTypes.node.isRequired,

  /**
     * div element the cell contents have been rendered into; useful for processing the rendered text
     */
  cellContentsElement: PropTypes.any.isRequired
};
export var DataGridControlColumn = function DataGridControlColumn() {
  return React.createElement("div", null);
};
DataGridControlColumn.propTypes = {
  /**
     * Used as the React `key` when rendering content
     */
  id: PropTypes.string.isRequired,

  /**
     * Component to render in the column header
     */
  headerCellRender: PropTypes.elementType.isRequired,

  /**
     * Component to render for each row in the column
     */
  rowCellRender: PropTypes.oneOfType([PropTypes.func.isRequired, PropTypes.func.isRequired]).isRequired,

  /**
     * Width of the column, uses are unable to change this
     */
  width: PropTypes.number.isRequired
};
export var DataGridToolBarVisibilityColumnSelectorOptions = () => <div />;
DataGridToolBarVisibilityColumnSelectorOptions.propTypes = {
  /**
     * When `false`, removes the ability to show & hide columns through the UI
     */
  allowHide: PropTypes.bool,

  /**
     * When `false`, removes the ability to re-order columns through the UI
     */
  allowReorder: PropTypes.bool
};

try {
  // @ts-ignore
  DataGridColumn.displayName = "DataGridColumn"; // @ts-ignore

  DataGridColumn.__docgenInfo = {
    "description": "",
    "displayName": "DataGridColumn",
    "props": {
      "id": {
        "defaultValue": null,
        "description": "The unique identifier for this column",
        "name": "id",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "display": {
        "defaultValue": null,
        "description": "A `ReactNode` used when rendering the column header. When providing complicated content, please make sure to utilize CSS to respect truncation as space allows. Check the docs example.",
        "name": "display",
        "required": false,
        "type": {
          "name": "ReactNode"
        }
      },
      "schema": {
        "defaultValue": null,
        "description": "A Schema to use for the column. Built-in values are ['boolean', 'currency', 'datetime', 'numeric', 'json'] but can be expanded by defining your own #EuiDataGrid `schemaDetectors` (for in-memory detection). In general, it is advised to pass in a value here when you are sure of the schema ahead of time, so that you don't need to rely on the automatic detection.",
        "name": "schema",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "isExpandable": {
        "defaultValue": null,
        "description": "Defauls to true. Defines shether or not the column's cells can be expanded with a popup onClick / keydown.",
        "name": "isExpandable",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "isResizable": {
        "defaultValue": null,
        "description": "Whether this column's width can be changed by the user, defaults to true",
        "name": "isResizable",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "initialWidth": {
        "defaultValue": null,
        "description": "Initial width (in pixels) of the column",
        "name": "initialWidth",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "isSortable": {
        "defaultValue": null,
        "description": "Whether this column is sortable",
        "name": "isSortable",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "defaultSortDirection": {
        "defaultValue": null,
        "description": "Default sort direction of the column",
        "name": "defaultSortDirection",
        "required": false,
        "type": {
          "name": "\"asc\" | \"desc\" | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridColumn"] = {
      docgenInfo: DataGridColumn.__docgenInfo,
      name: "DataGridColumn",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridColumn"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  DataGridPagination.displayName = "DataGridPagination"; // @ts-ignore

  DataGridPagination.__docgenInfo = {
    "description": "",
    "displayName": "DataGridPagination",
    "props": {
      "pageIndex": {
        "defaultValue": null,
        "description": "The index of the current page, starts at 0 for the first page",
        "name": "pageIndex",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "pageSize": {
        "defaultValue": null,
        "description": "How many rows should initially be shown per page",
        "name": "pageSize",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "pageSizeOptions": {
        "defaultValue": null,
        "description": "An array of page sizes the user can select from",
        "name": "pageSizeOptions",
        "required": true,
        "type": {
          "name": "number[]"
        }
      },
      "onChangeItemsPerPage": {
        "defaultValue": null,
        "description": "A callback for when the user changes the page size selection",
        "name": "onChangeItemsPerPage",
        "required": true,
        "type": {
          "name": "(itemsPerPage: number) => void"
        }
      },
      "onChangePage": {
        "defaultValue": null,
        "description": "A callback for when the current page index changes",
        "name": "onChangePage",
        "required": true,
        "type": {
          "name": "(pageIndex: number) => void"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridPagination"] = {
      docgenInfo: DataGridPagination.__docgenInfo,
      name: "DataGridPagination",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridPagination"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  DataGridSorting.displayName = "DataGridSorting"; // @ts-ignore

  DataGridSorting.__docgenInfo = {
    "description": "",
    "displayName": "DataGridSorting",
    "props": {
      "onSort": {
        "defaultValue": null,
        "description": "A function that receives updated column sort details in response to user interactions in the toolbar controls",
        "name": "onSort",
        "required": true,
        "type": {
          "name": "(columns: { id: string; direction: \"asc\" | \"desc\"; }[]) => void"
        }
      },
      "columns": {
        "defaultValue": null,
        "description": "An array of the column ids currently being sorted and their sort direction. The array order determines the sort order. `{ id: 'A'; direction: 'asc' }`",
        "name": "columns",
        "required": true,
        "type": {
          "name": "{ id: string; direction: \"asc\" | \"desc\"; }[]"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridSorting"] = {
      docgenInfo: DataGridSorting.__docgenInfo,
      name: "DataGridSorting",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridSorting"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  DataGridInMemory.displayName = "DataGridInMemory"; // @ts-ignore

  DataGridInMemory.__docgenInfo = {
    "description": "",
    "displayName": "DataGridInMemory",
    "props": {
      "level": {
        "defaultValue": null,
        "description": "Given the data flow Sorting->Pagination:\nEach step can be performed by service calls or in-memory by the grid.\nHowever, we cannot allow any service calls after an in-memory operation.\nE.g. if Pagination requires a service call the grid cannot perform\nin-memory Sorting. This means a single value representing the\nservice / in-memory boundary can be used. Thus there are four states for in-memory's level:\n \"enhancements\" - no in-memory operations, but use the available data to enhance the grid\n \"pagination\" - only pagination is performed in-memory\n \"sorting\" - sorting & pagination is performed in-memory",
        "name": "level",
        "required": true,
        "type": {
          "name": "enum",
          "value": [{
            "value": "\"enhancements\""
          }, {
            "value": "\"pagination\""
          }, {
            "value": "\"sorting\""
          }]
        }
      },
      "skipColumns": {
        "defaultValue": null,
        "description": "An array of column ids for the in-memory processing to skip",
        "name": "skipColumns",
        "required": false,
        "type": {
          "name": "string[] | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridInMemory"] = {
      docgenInfo: DataGridInMemory.__docgenInfo,
      name: "DataGridInMemory",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridInMemory"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  DataGridStyle.displayName = "DataGridStyle"; // @ts-ignore

  DataGridStyle.__docgenInfo = {
    "description": "",
    "displayName": "DataGridStyle",
    "props": {
      "fontSize": {
        "defaultValue": null,
        "description": "Size of fonts used within the row and column cells",
        "name": "fontSize",
        "required": false,
        "type": {
          "name": "\"s\" | \"m\" | \"l\" | undefined"
        }
      },
      "border": {
        "defaultValue": null,
        "description": "Border uses for the row and column cells",
        "name": "border",
        "required": false,
        "type": {
          "name": "\"none\" | \"horizontal\" | \"all\" | undefined"
        }
      },
      "stripes": {
        "defaultValue": null,
        "description": "If set to true, rows will alternate zebra striping for clarity",
        "name": "stripes",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "header": {
        "defaultValue": null,
        "description": "Visual style for the column headers. Recommendation is to use the `underline` style in times when #EuiDataGrid `toolbarVisibility` is set to `false`.",
        "name": "header",
        "required": false,
        "type": {
          "name": "\"shade\" | \"underline\" | undefined"
        }
      },
      "rowHover": {
        "defaultValue": null,
        "description": "Will define what visual style to show on row hover",
        "name": "rowHover",
        "required": false,
        "type": {
          "name": "\"none\" | \"highlight\" | undefined"
        }
      },
      "cellPadding": {
        "defaultValue": null,
        "description": "Defines the padding with the row and column cells",
        "name": "cellPadding",
        "required": false,
        "type": {
          "name": "\"s\" | \"m\" | \"l\" | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridStyle"] = {
      docgenInfo: DataGridStyle.__docgenInfo,
      name: "DataGridStyle",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridStyle"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  DataGridToolbarVisibilityOptions.displayName = "DataGridToolbarVisibilityOptions"; // @ts-ignore

  DataGridToolbarVisibilityOptions.__docgenInfo = {
    "description": "",
    "displayName": "DataGridToolbarVisibilityOptions",
    "props": {
      "showColumnSelector": {
        "defaultValue": null,
        "description": "Allows the ability for the user to hide fields and sort columns, boolean or a #EuiDataGridToolBarVisibilityColumnSelectorOptions",
        "name": "showColumnSelector",
        "required": false,
        "type": {
          "name": "boolean | EuiDataGridToolBarVisibilityColumnSelectorOptions | undefined"
        }
      },
      "showStyleSelector": {
        "defaultValue": null,
        "description": "Allows the ability for the user to set the grid density. If on, this merges against what is provided in #EuiDataGridStyle",
        "name": "showStyleSelector",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showSortSelector": {
        "defaultValue": null,
        "description": "Allows the ability for the user to sort rows based upon column values",
        "name": "showSortSelector",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "showFullScreenSelector": {
        "defaultValue": null,
        "description": "Allows user to be able to full screen the data grid. If set to `false` make sure your grid fits within a large enough panel to still show the other controls.",
        "name": "showFullScreenSelector",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "additionalControls": {
        "defaultValue": null,
        "description": "Will place any passed node into the toolbar in front of the fullscreen button. Recommend using EuiButtonEmpty with the props shown in the examples.",
        "name": "additionalControls",
        "required": false,
        "type": {
          "name": "ReactNode"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridToolbarVisibilityOptions"] = {
      docgenInfo: DataGridToolbarVisibilityOptions.__docgenInfo,
      name: "DataGridToolbarVisibilityOptions",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridToolbarVisibilityOptions"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  DataGridCellValueElement.displayName = "DataGridCellValueElement"; // @ts-ignore

  DataGridCellValueElement.__docgenInfo = {
    "description": "",
    "displayName": "DataGridCellValueElement",
    "props": {
      "rowIndex": {
        "defaultValue": null,
        "description": "index of the row being rendered, 0 represents the first row. This index always includes\npagination offset, meaning the first rowIndex in a grid is `pagination.pageIndex * pagination.pageSize`\nso take care if you need to adjust the rowIndex to fit your data",
        "name": "rowIndex",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "columnId": {
        "defaultValue": null,
        "description": "id of the column being rendered, the value comes from the #EuiDataGridColumn `id`",
        "name": "columnId",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "setCellProps": {
        "defaultValue": null,
        "description": "callback function to set custom props & attributes on the cell's wrapping `div` element;\nit's best to wrap calls to `setCellProps` in a `useEffect` hook",
        "name": "setCellProps",
        "required": true,
        "type": {
          "name": "(props: CommonProps & HTMLAttributes<HTMLDivElement>) => void"
        }
      },
      "isExpandable": {
        "defaultValue": null,
        "description": "whether or not the cell is expandable, comes from the #EuiDataGridColumn `isExpandable` which defaults to `true`",
        "name": "isExpandable",
        "required": true,
        "type": {
          "name": "boolean"
        }
      },
      "isExpanded": {
        "defaultValue": null,
        "description": "whether or not the cell is expanded",
        "name": "isExpanded",
        "required": true,
        "type": {
          "name": "boolean"
        }
      },
      "isDetails": {
        "defaultValue": null,
        "description": "when rendering the cell, `isDetails` is false; when the cell is expanded, `renderCellValue` is called again to render into the details popover and `isDetails` is true",
        "name": "isDetails",
        "required": true,
        "type": {
          "name": "boolean"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridCellValueElement"] = {
      docgenInfo: DataGridCellValueElement.__docgenInfo,
      name: "DataGridCellValueElement",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridCellValueElement"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  DataGridSchemaDetector.displayName = "DataGridSchemaDetector"; // @ts-ignore

  DataGridSchemaDetector.__docgenInfo = {
    "description": "",
    "displayName": "DataGridSchemaDetector",
    "props": {
      "type": {
        "defaultValue": null,
        "description": "The name of this data type, matches #EuiDataGridColumn / schema `schema`",
        "name": "type",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "detector": {
        "defaultValue": null,
        "description": "The function given the text value of a cell and returns a score of [0...1] of how well the value matches this data type",
        "name": "detector",
        "required": true,
        "type": {
          "name": "(value: string) => number"
        }
      },
      "comparator": {
        "defaultValue": null,
        "description": "A custom comparator function when performing in-memory sorting on this data type, takes `(a: string, b: string, direction: 'asc' | 'desc) => -1 | 0 | 1`",
        "name": "comparator",
        "required": false,
        "type": {
          "name": "((a: string, b: string, direction: \"asc\" | \"desc\") => 0 | 1 | -1) | undefined"
        }
      },
      "icon": {
        "defaultValue": null,
        "description": "The icon used to visually represent this data type. Accepts any `EuiIcon IconType`.",
        "name": "icon",
        "required": true,
        "type": {
          "name": "IconType"
        }
      },
      "color": {
        "defaultValue": null,
        "description": "The color associated with this data type; it's used to color the icon token",
        "name": "color",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "sortTextAsc": {
        "defaultValue": null,
        "description": "Text for how to represent an ascending sort of this data type, e.g. 'A -> Z'",
        "name": "sortTextAsc",
        "required": true,
        "type": {
          "name": "ReactNode"
        }
      },
      "sortTextDesc": {
        "defaultValue": null,
        "description": "Text for how to represent a descending sort of this data type, e.g. 'Z -> A'",
        "name": "sortTextDesc",
        "required": true,
        "type": {
          "name": "ReactNode"
        }
      },
      "isSortable": {
        "defaultValue": null,
        "description": "Whether this column is sortable (defaults to true)",
        "name": "isSortable",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "defaultSortDirection": {
        "defaultValue": null,
        "description": "Default sort direction of the column",
        "name": "defaultSortDirection",
        "required": false,
        "type": {
          "name": "\"asc\" | \"desc\" | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridSchemaDetector"] = {
      docgenInfo: DataGridSchemaDetector.__docgenInfo,
      name: "DataGridSchemaDetector",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridSchemaDetector"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  DataGridColumnVisibility.displayName = "DataGridColumnVisibility"; // @ts-ignore

  DataGridColumnVisibility.__docgenInfo = {
    "description": "",
    "displayName": "DataGridColumnVisibility",
    "props": {
      "visibleColumns": {
        "defaultValue": null,
        "description": "An array of #EuiDataGridColumn `id`s dictating the order and visibility of columns.",
        "name": "visibleColumns",
        "required": true,
        "type": {
          "name": "string[]"
        }
      },
      "setVisibleColumns": {
        "defaultValue": null,
        "description": "A callback for when a column's visibility or order is modified by the user.",
        "name": "setVisibleColumns",
        "required": true,
        "type": {
          "name": "(visibleColumns: string[]) => void"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridColumnVisibility"] = {
      docgenInfo: DataGridColumnVisibility.__docgenInfo,
      name: "DataGridColumnVisibility",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridColumnVisibility"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  DataGridPopoverContent.displayName = "DataGridPopoverContent"; // @ts-ignore

  DataGridPopoverContent.__docgenInfo = {
    "description": "",
    "displayName": "DataGridPopoverContent",
    "props": {
      "children": {
        "defaultValue": null,
        "description": "your `cellValueRenderer` as a ReactElement; allows wrapping the rendered content: `({children}) => <div>{children}</div>`",
        "name": "children",
        "required": false,
        "type": {
          "name": "ReactNode"
        }
      },
      "cellContentsElement": {
        "defaultValue": null,
        "description": "div element the cell contents have been rendered into; useful for processing the rendered text",
        "name": "cellContentsElement",
        "required": true,
        "type": {
          "name": "HTMLDivElement"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridPopoverContent"] = {
      docgenInfo: DataGridPopoverContent.__docgenInfo,
      name: "DataGridPopoverContent",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridPopoverContent"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  DataGridControlColumn.displayName = "DataGridControlColumn"; // @ts-ignore

  DataGridControlColumn.__docgenInfo = {
    "description": "",
    "displayName": "DataGridControlColumn",
    "props": {
      "id": {
        "defaultValue": null,
        "description": "Used as the React `key` when rendering content",
        "name": "id",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "headerCellRender": {
        "defaultValue": null,
        "description": "Component to render in the column header",
        "name": "headerCellRender",
        "required": true,
        "type": {
          "name": "ComponentType<{}>"
        }
      },
      "rowCellRender": {
        "defaultValue": null,
        "description": "Component to render for each row in the column",
        "name": "rowCellRender",
        "required": true,
        "type": {
          "name": "((props: EuiDataGridCellValueElementProps) => ReactElement<any, string | ((props: any) => ReactElement<any, string | ... | (new (props: any) => Component<any, any, any>)> | null) | (new (props: any) => Component<...>)> | null) | (new (props: EuiDataGridCellValueElementProps) => Component<...>) | ((props: EuiDataGrid..."
        }
      },
      "width": {
        "defaultValue": null,
        "description": "Width of the column, uses are unable to change this",
        "name": "width",
        "required": true,
        "type": {
          "name": "number"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridControlColumn"] = {
      docgenInfo: DataGridControlColumn.__docgenInfo,
      name: "DataGridControlColumn",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridControlColumn"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  DataGridToolBarVisibilityColumnSelectorOptions.displayName = "DataGridToolBarVisibilityColumnSelectorOptions"; // @ts-ignore

  DataGridToolBarVisibilityColumnSelectorOptions.__docgenInfo = {
    "description": "",
    "displayName": "DataGridToolBarVisibilityColumnSelectorOptions",
    "props": {
      "allowHide": {
        "defaultValue": null,
        "description": "When `false`, removes the ability to show & hide columns through the UI",
        "name": "allowHide",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "allowReorder": {
        "defaultValue": null,
        "description": "When `false`, removes the ability to re-order columns through the UI",
        "name": "allowReorder",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src-docs/src/views/datagrid/props.tsx#DataGridToolBarVisibilityColumnSelectorOptions"] = {
      docgenInfo: DataGridToolBarVisibilityColumnSelectorOptions.__docgenInfo,
      name: "DataGridToolBarVisibilityColumnSelectorOptions",
      path: "src-docs/src/views/datagrid/props.tsx#DataGridToolBarVisibilityColumnSelectorOptions"
    };
} catch (__react_docgen_typescript_loader_error) {}